/**
 * FieldSettingsTab — dynamic form builder for trainee fields.
 *
 * Admins can create / edit / delete fields, choose type, and add dropdown
 * options. System fields are read-only-marked but can still be hidden.
 */
import { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Eye, EyeOff, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import AttendanceStatusesPanel from './AttendanceStatusesPanel';
import DepartmentsPanel from './DepartmentsPanel';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'numeric', label: 'Numeric' },
  { value: 'email', label: 'Email' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'score', label: 'Score' },
  { value: 'calculated', label: 'Auto-Calculated' },
];

const SECTIONS = [
  { value: 'basic', label: 'Basic Information' },
  { value: 'status', label: 'Status' },
  { value: 'performance', label: 'Performance' },
  { value: 'custom', label: 'Custom' },
];

const EMPTY = {
  label: '', field_type: 'text', section: 'custom', scope: '', description: '',
  options: [], min_value: '', max_value: '',
  is_required: false, is_visible: true,
};

export default function FieldSettingsTab() {
  const { get, post, put, del } = useApi();
  const { hasRole } = useAuth();
  const canEdit = hasRole('org_admin');

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get('/trainees/fields');
      setFields(res.fields || []);
    } catch { toast.error('Failed to load fields'); }
    finally { setLoading(false); }
  }, [get]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpenDialog(true); };
  const openEdit = (f) => {
    setEditing(f);
    setForm({
      label: f.label, field_type: f.field_type, section: f.section, scope: f.scope || '',
      description: f.description || '',
      options: f.options || [], min_value: f.min_value ?? '', max_value: f.max_value ?? '',
      is_required: f.is_required, is_visible: f.is_visible,
    });
    setOpenDialog(true);
  };

  const onSave = async () => {
    if (!form.label.trim()) { toast.error('Label is required'); return; }
    const payload = {
      label: form.label.trim(),
      field_type: form.field_type,
      section: form.section,
      scope: form.scope || null,
      description: form.description || null,
      options: form.options || [],
      min_value: form.min_value === '' ? null : Number(form.min_value),
      max_value: form.max_value === '' ? null : Number(form.max_value),
      is_required: form.is_required,
      is_visible: form.is_visible,
    };
    try {
      if (editing) {
        await put(`/trainees/fields/${editing.field_id}`, payload);
        toast.success('Field updated');
      } else {
        await post('/trainees/fields', payload);
        toast.success('Field created');
      }
      setOpenDialog(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const toggleVisible = async (f) => {
    try {
      await put(`/trainees/fields/${f.field_id}`, { is_visible: !f.is_visible });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await del(`/trainees/fields/${confirmDelete.field_id}`);
      toast.success('Field deleted');
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const grouped = SECTIONS.map(s => ({
    ...s,
    items: fields.filter(f => f.section === s.value),
  }));

  return (
    <div className="space-y-4" data-testid="field-settings-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {fields.length} fields configured ({fields.filter(f => f.is_system).length} system)
        </p>
        {canEdit && (
          <Button onClick={openCreate} data-testid="create-field-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Field
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-6">
          {grouped.map(section => (
            <div key={section.value}>
              <h3 className="font-display text-base font-semibold mb-2">{section.label}</h3>
              {section.items.length === 0 ? (
                <p className="text-xs text-muted-foreground">No fields in this section.</p>
              ) : (
                <Card className="divide-y">
                  {section.items.map(f => (
                    <div key={f.field_id} className="flex items-center justify-between p-3 hover:bg-muted/40"
                         data-testid={`field-row-${f.field_id}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{f.label}</span>
                            {f.is_required && <Star className="w-3 h-3 text-amber-500" />}
                            {f.is_system && <Badge variant="outline" className="text-xs">System</Badge>}
                            {!f.is_visible && <Badge variant="secondary" className="text-xs">Hidden</Badge>}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {f.field_key} · {FIELD_TYPES.find(t => t.value === f.field_type)?.label || f.field_type}
                            {f.options?.length > 0 && ` · ${f.options.length} options`}
                          </span>
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => toggleVisible(f)}
                                  data-testid={`toggle-visible-${f.field_id}`}>
                            {f.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(f)}
                                  data-testid={`edit-field-${f.field_id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {!f.is_system && (
                            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(f)}
                                    data-testid={`delete-field-${f.field_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </Card>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="pt-6 border-t">
        <DepartmentsPanel />
      </div>

      <div className="pt-6 border-t">
        <AttendanceStatusesPanel />
      </div>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-lg" data-testid="field-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Field' : 'New Field'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Label *</Label>
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                     placeholder="e.g. Trainer Notes" data-testid="field-input-label" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.field_type} onValueChange={v => setForm({ ...form, field_type: v })}
                        disabled={!!editing}>
                  <SelectTrigger data-testid="field-input-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Section</Label>
                <Select value={form.section} onValueChange={v => setForm({ ...form, section: v })}>
                  <SelectTrigger data-testid="field-input-section"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SECTIONS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Applies to</Label>
              <Select value={form.scope || '__auto__'} onValueChange={v => setForm({ ...form, scope: v === '__auto__' ? '' : v })}>
                <SelectTrigger data-testid="field-input-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">
                    By section ({form.section === 'basic' ? 'person — shared across batches' : 'batch — fresh per batch'})
                  </SelectItem>
                  <SelectItem value="person">Person — one value shared across all batches</SelectItem>
                  <SelectItem value="batch">Batch — separate value per batch, starts blank</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Status &amp; performance values are per batch, so a returning agent starts a new batch clean while earlier batches keep their record.
              </p>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description}
                        onChange={e => setForm({ ...form, description: e.target.value })}
                        rows={2} />
            </div>
            {['dropdown', 'multiselect', 'radio'].includes(form.field_type) && (
              <div>
                <Label>Options (one per line)</Label>
                <Textarea value={(form.options || []).join('\n')}
                          onChange={e => setForm({
                            ...form,
                            options: e.target.value.split('\n').map(s => s.trim()).filter(Boolean),
                          })}
                          rows={4} data-testid="field-input-options" />
              </div>
            )}
            {['numeric', 'percentage', 'score'].includes(form.field_type) && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Min</Label>
                  <Input type="number" value={form.min_value}
                         onChange={e => setForm({ ...form, min_value: e.target.value })} />
                </div>
                <div>
                  <Label>Max</Label>
                  <Input type="number" value={form.max_value}
                         onChange={e => setForm({ ...form, max_value: e.target.value })} />
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <Label>Required</Label>
              <Switch checked={form.is_required}
                      onCheckedChange={v => setForm({ ...form, is_required: v })}
                      data-testid="field-input-required" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Visible</Label>
              <Switch checked={form.is_visible}
                      onCheckedChange={v => setForm({ ...form, is_visible: v })}
                      data-testid="field-input-visible" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={onSave} data-testid="field-save-btn">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete field?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{confirmDelete?.label}</strong> and strip its values
              from every trainee record. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="confirm-delete-field-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
